import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { FiltrosDeOs, RegimeDaOs, SituacaoDaOs } from '../../../types/ordemDeServico'
import { SITUACAO_DA_OS } from '../situacaoDaOs'

const REGIMES: RegimeDaOs[] = ['SUSTENTACAO_INFRAESTRUTURA', 'PROJETO_PONTO_DE_FUNCAO']

/**
 * Os filtros vivem na URL, e não no estado do componente.
 *
 * É o que torna o recorte colável: "veja as ordens de junho" é um endereço que se manda por e-mail,
 * e recarregar a página não desfaz o que a pessoa escolheu. O sistema anterior guardava filtro em
 * sessão, e duas abas abertas mostravam recortes diferentes com a mesma barra de endereço.
 *
 * Valor desconhecido na URL é **descartado**, nunca repassado ao servidor: `?situacao=ATRASADA`
 * viraria um 400 no meio de uma tela que carregava bem, e a pessoa não teria como saber por quê.
 *
 * @returns os filtros correntes e como alterá-los
 */
export function useFiltrosDeOsNaUrl() {
  const [parametros, definirParametros] = useSearchParams()

  const filtros = useMemo<FiltrosDeOs>(() => {
    const regime = parametros.get('regime') ?? ''
    const situacao = parametros.get('situacao') ?? ''
    const pagina = Number(parametros.get('pagina') ?? '1')
    return {
      competencia: /^\d{4}-\d{2}$/.test(parametros.get('competencia') ?? '') ? parametros.get('competencia')! : '',
      regime: REGIMES.includes(regime as RegimeDaOs) ? (regime as RegimeDaOs) : '',
      situacao: situacao in SITUACAO_DA_OS ? (situacao as SituacaoDaOs) : '',
      pagina: Number.isInteger(pagina) && pagina > 0 ? pagina : 1,
    }
  }, [parametros])

  const alterar = useCallback(
    (mudanca: Partial<FiltrosDeOs>) => {
      // Mudar qualquer critério volta para a primeira página. Sem isto, filtrar estando na página
      // 3 devolve uma lista vazia que se lê como "não há nenhuma".
      const proximos = { ...filtros, pagina: 1, ...mudanca }
      const busca = new URLSearchParams()
      if (proximos.competencia) {
        busca.set('competencia', proximos.competencia)
      }
      if (proximos.regime) {
        busca.set('regime', proximos.regime)
      }
      if (proximos.situacao) {
        busca.set('situacao', proximos.situacao)
      }
      if (proximos.pagina > 1) {
        busca.set('pagina', String(proximos.pagina))
      }
      definirParametros(busca, { replace: true })
    },
    [filtros, definirParametros],
  )

  const limpar = useCallback(() => definirParametros(new URLSearchParams(), { replace: true }), [definirParametros])

  const temFiltro = Boolean(filtros.competencia || filtros.regime || filtros.situacao)

  return { filtros, alterar, limpar, temFiltro }
}
